import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTheme } from "./theme";

interface Step {
  id: number;
  title: string;
  headline: string;
  description: string;
  keyFeatures: string[];
  metrics?: { label: string; value: string; helper?: string }[];
  footer?: string;
}

const steps: Step[] = [
  {
    id: 1,
    title: "Enter your URL",
    headline: "Tell us what to scan",
    description:
      "Paste a production, staging, or preview link. We tidy the URL and prepare a safe, private check.",
    keyFeatures: [
      "No code or repo access",
      "Live queue status",
      "Respectful crawl to avoid noise",
    ],
    footer: "Start in seconds — nothing to install.",
  },
  {
    id: 2,
    title: "LaunchSafely runs",
    headline: "Playwright runner with our custom suite",
    description:
      "A secure Chromium session runs our curated smoke checks and captures helpful logs for quick diagnosis.",
    keyFeatures: [
      "Custom smoke playbook",
      "Primary flows + smart checks",
      "Screenshots and trace for replay",
    ],
    footer: "Isolated worker. Artifacts are auto‑pruned.",
  },
  {
    id: 3,
    title: "View results",
    headline: "Clear pass/fail with context",
    description:
      "See what passed, what failed, and why. Open screenshots or traces to replay.",
    keyFeatures: [
      "Coverage for core flows",
      "Actionable summaries",
      "Shareable artifacts",
    ],
    footer: "Most runs finish in under 2 minutes.",
  },
  {
    id: 4,
    title: "Close the loop",
    headline: "Fix issues or bring in an expert",
    description:
      "Follow our suggestions, or work with one of our senior QA experts to extend coverage and triage bugs.",
    keyFeatures: ["Guided fixes", "Hire‑a‑tester", "Baselines coming soon"],
  },
];

export default function HowItWorks() {
  const { theme } = useTheme();
  const palette = theme.palette;
  const [activeId, setActiveId] = useState<number>(steps[0].id);
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const [showScrollHint, setShowScrollHint] = useState(false);
  const stepListRef = useRef<HTMLDivElement | null>(null);
  const stepRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const scrollFrameRef = useRef<number | null>(null);
  const userScrollActiveRef = useRef(false);
  const userScrollTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const activeStep = useMemo(
    () => steps.find((step) => step.id === activeId) ?? steps[0],
    [activeId]
  );
  const isStacked = viewportWidth < 900;

  useEffect(() => {
    if (isStacked) {
      setShowScrollHint(true);
    } else {
      setShowScrollHint(false);
    }
  }, [isStacked]);

  const handleStepTabsScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      if (showScrollHint && target.scrollLeft > 12) {
        setShowScrollHint(false);
      }

      if (!isStacked) return;

      userScrollActiveRef.current = true;
      if (typeof window !== "undefined") {
        if (userScrollTimeoutRef.current !== null) {
          window.clearTimeout(userScrollTimeoutRef.current);
        }
        userScrollTimeoutRef.current = window.setTimeout(() => {
          userScrollActiveRef.current = false;
          userScrollTimeoutRef.current = null;
        }, 180);
      }

      if (typeof window === "undefined") return;
      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
      scrollFrameRef.current = window.requestAnimationFrame(() => {
        const container = target;
        const containerCenter =
          container.scrollLeft + container.offsetWidth / 2;
        let nearestId = activeId;
        let nearestDistance = Number.POSITIVE_INFINITY;

        for (const step of steps) {
          const ref = stepRefs.current[step.id];
          if (!ref) continue;
          const elCenter = ref.offsetLeft + ref.offsetWidth / 2;
          const distance = Math.abs(elCenter - containerCenter);
          if (distance < nearestDistance - 1) {
            nearestDistance = distance;
            nearestId = step.id;
          }
        }

        if (nearestId !== activeId) {
          setActiveId(nearestId);
        }
        scrollFrameRef.current = null;
      });
    },
    [activeId, isStacked, showScrollHint]
  );

  useEffect(() => {
    if (!isStacked) return;
    if (userScrollActiveRef.current) return;
    const container = stepListRef.current;
    const activeEl = stepRefs.current[activeId];
    if (!container || !activeEl) return;
    const desired =
      activeEl.offsetLeft +
      activeEl.offsetWidth / 2 -
      container.offsetWidth / 2;
    const maxScroll = container.scrollWidth - container.offsetWidth;
    const nextScroll = Math.max(0, Math.min(maxScroll, desired));
    container.scrollTo({ left: nextScroll, behavior: "smooth" });
  }, [activeId, isStacked]);

  useEffect(() => {
    return () => {
      if (typeof window === "undefined") return;
      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
      if (userScrollTimeoutRef.current !== null) {
        window.clearTimeout(userScrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <section
      id="how-it-works"
      style={{
        padding: "2.5rem 1rem",
        background: palette.backgroundAlt,
        color: palette.textPrimary,
      }}
    >
      <div
        style={{
          maxWidth: "1040px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: "2.5rem",
        }}
      >
        <header style={{ textAlign: "center" }}>
          <h2
            style={{
              fontSize: "2.25rem",
              fontWeight: 800,
              marginBottom: "0.75rem",
            }}
          >
            How LaunchSafely Works
          </h2>
          <p
            style={{
              maxWidth: "620px",
              margin: "0 auto",
              color: palette.textMuted,
              fontSize: "1.05rem",
              lineHeight: 1.6,
            }}
          >
            Our streamlined process gets you from URL to actionable QA insights
            in just four focused steps.
          </p>
        </header>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isStacked ? "1fr" : "minmax(220px, 320px) 1fr",
            gap: "1.75rem",
          }}
        >
          <div
            ref={stepListRef}
            style={{
              display: "flex",
              flexDirection: isStacked ? "row" : "column",
              overflowX: isStacked ? "auto" : "visible",
              gap: "1rem",
              paddingBottom: isStacked ? "0.5rem" : 0,
              scrollSnapType: isStacked ? "x mandatory" : undefined,
              WebkitOverflowScrolling: isStacked ? "touch" : undefined,
              scrollPadding: isStacked ? "0 1rem" : undefined,
            }}
            onScroll={isStacked ? handleStepTabsScroll : undefined}
          >
            {steps.map((step) => {
              const isActive = step.id === activeId;
              return (
                <button
                  key={step.id}
                  ref={(el) => {
                    stepRefs.current[step.id] = el;
                  }}
                  onClick={() => setActiveId(step.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.85rem",
                    padding: "0.85rem 1.1rem",
                    borderRadius: "14px",
                    border: isActive
                      ? `1px solid ${palette.accent}`
                      : `1px solid ${palette.border}`,
                    background: isActive
                      ? theme.heroBackground
                      : palette.cardAlt,
                    color: palette.textPrimary,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "transform 0.15s ease, box-shadow 0.15s ease",
                    boxShadow: isActive
                      ? "0 12px 24px rgba(47,145,209,0.25)"
                      : "none",
                    minWidth: isStacked ? 220 : undefined,
                    flexShrink: isStacked ? 0 : 1,
                    scrollSnapAlign: isStacked ? "center" : undefined,
                    scrollSnapStop: isStacked ? "always" : undefined,
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "12px",
                      background: palette.accentGradient,
                      color: palette.textOnAccent,
                      fontWeight: 700,
                      fontSize: "1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {step.id}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{step.title}</div>
                    {step.id === activeId && (
                      <div
                        style={{
                          color: palette.textMuted,
                          fontSize: "0.85rem",
                          marginTop: "0.2rem",
                        }}
                      >
                        Step {step.id} of {steps.length}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {showScrollHint && isStacked && (
            <div
              style={{
                marginTop: "0.25rem",
                fontSize: "0.85rem",
                color: palette.textMuted,
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                userSelect: "none",
                justifyContent: "center",
              }}
            >
              <span
                aria-hidden="true"
                style={{ fontSize: "1rem", letterSpacing: "0.2rem" }}
              >
                {"< >"}
              </span>
              Swipe through the steps
            </div>
          )}

          <div
            style={{
              background: theme.heroBackground,
              borderRadius: "20px",
              border: `1px solid ${palette.borderSoft}`,
              boxShadow: palette.shadow,
              padding: "2rem",
              display: "flex",
              flexDirection: "column",
              gap: "1.5rem",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {theme.mode === "dark" && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  background:
                    "radial-gradient(circle at 12% 18%, rgba(101, 192, 221, 0.18), transparent 45%), radial-gradient(circle at 88% 28%, rgba(47, 145, 209, 0.12), transparent 42%)",
                }}
              />
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "0.75rem",
              }}
            >
              <div>
                <h3
                  style={{
                    marginTop: "0.25rem",
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  {activeStep.headline}
                </h3>
                <p style={{ color: palette.textMuted, lineHeight: 1.6 }}>
                  {activeStep.description}
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  alignItems: "center",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                }}
              ></div>
            </div>

            <div>
              <div
                style={{
                  fontWeight: 600,
                  marginBottom: "0.75rem",
                  color: palette.textSecondary,
                }}
              >
                Key Features
              </div>
              <ul
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "0.75rem",
                  padding: 0,
                  margin: 0,
                  listStyle: "none",
                }}
              >
                {activeStep.keyFeatures.map((feature) => (
                  <li
                    key={feature}
                    style={{
                      border: `1px solid ${palette.border}`,
                      borderRadius: "12px",
                      padding: "0.75rem 0.85rem",
                      background: palette.cardAlt,
                      fontSize: "0.9rem",
                    }}
                  >
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {activeStep.metrics && activeStep.metrics.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "1rem",
                }}
              >
                {activeStep.metrics.map((metric) => (
                  <div
                    key={metric.label + metric.value}
                    style={{
                      borderRadius: "14px",
                      border: `1px solid ${palette.border}`,
                      background: palette.cardAlt,
                      padding: "1rem",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: palette.textMuted,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                      }}
                    >
                      {metric.label}
                    </div>
                    <div
                      style={{
                        fontSize: "1.4rem",
                        fontWeight: 700,
                        margin: "0.35rem 0",
                      }}
                    >
                      {metric.value}
                    </div>
                    {metric.helper && (
                      <div
                        style={{
                          fontSize: "0.85rem",
                          color: palette.textMuted,
                        }}
                      >
                        {metric.helper}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeStep.footer && (
              <div style={{ fontSize: "0.9rem", color: palette.textSecondary }}>
                {activeStep.footer}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
